import { Grid, Paper, Typography, Box } from '@mui/material';
import {
  Button,
  CellphoneWrapper,
  CPFWrapper,
  DateWrapper,
  SelectWrapper,
  Textfield,
  UploadButton,
} from 'components/FormsUI';
import { FormMessage } from 'components/Message';
import { LinearProgress } from 'components/LinearProgress';
import { Form, Formik } from 'formik';
import { Format } from 'helpers/Format';
import { useUser } from 'hooks/useUser';
import { gender, presencialTreatment, specialization, uf } from 'mocks';
import { FC, useState } from 'react';
import { vetUpdateValidation } from 'schemas';
import { VetService } from 'services/vet.service';

const LeftFormData: FC<any> = ({ loading, user }) => {
  return (
    <Paper variant="outlined" sx={{ border: '2px solid #FBB45F' }}>
      {loading && <LinearProgress />}
      <Box p={2}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography
              variant="button"
              color="inherit"
              noWrap
              sx={{ fontSize: 20, fontWeight: 500 }}
            >
              DADOS PESSOAIS
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Textfield name="name" id="name" label="NOME COMPLETO" />
          </Grid>
          <Grid item xs={6}>
            <DateWrapper
              name="birthday"
              id="birthday"
              label="DATA DE NASCIMENTO"
            />
          </Grid>
          <Grid item xs={6}>
            <CPFWrapper name="cpf" id="cpf" label="CPF" />
          </Grid>
          <Grid item xs={6}>
            <SelectWrapper
              name="gender"
              id="gender"
              label="GÊNERO"
              options={gender}
            />
          </Grid>
          <Grid item xs={6}>
            <Textfield name="email" id="email" label="EMAIL" />
          </Grid>
          <Grid item xs={6}>
            <CellphoneWrapper name="phone" id="phone" label="CELULAR" />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="button">ANEXAR DOC. IDENTIFICAÇÃO</Typography>
          </Grid>
          <Grid item xs={12}>
            <UploadButton
              id="idDoc"
              name="idDoc"
              title={'ANEXAR DOC. IDENTIFICAÇÃO'}
            />
          </Grid>
          <Grid item xs={12}>
            <Box pt={4}>
              <Typography
                variant="button"
                color="inherit"
                noWrap
                sx={{ fontSize: 20, fontWeight: 500 }}
              >
                DADOS PROFISSIONAIS
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Textfield name="crmv" id="crmv" label="CRMV" />
          </Grid>
          <Grid item xs={6}>
            <SelectWrapper name="uf" id="uf" label="UF" options={uf} />
          </Grid>
          <Grid item xs={6}>
            <SelectWrapper
              name="specialization"
              id="specialization"
              label="ESPECIALIZAÇÃO"
              options={specialization}
            />
          </Grid>
          <Grid item xs={12}>
            <UploadButton
              id="counselorCard"
              name="counselorCard"
              title={'ANEXAR CARTÃO DO CONSELHO'}
            />
          </Grid>
          <Grid item xs={12}>
            <SelectWrapper
              name="presencialTreatment"
              id="presencialTreatment"
              label="ATENDE PRESENCIALMENTE"
              options={presencialTreatment}
            />
          </Grid>
          <Grid item xs={12}>
            <Textfield
              name="clinicName"
              id="clinicName"
              label="NOME DA CLINICA"
            />
          </Grid>
          <Grid item xs={12}>
            <Textfield
              name="clinicAddress"
              id="clinicAddress"
              label="ENDEREÇO DA CLINICA"
            />
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

const RightFormData: FC<any> = ({ loading }) => {
  return (
    <Paper variant="outlined" sx={{ border: '2px solid #FBB45F' }}>
      {loading && <LinearProgress />}
      <Box p={2}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography
              variant="button"
              color="inherit"
              noWrap
              sx={{ fontSize: 20, fontWeight: 500 }}
            >
              DADOS PESSOAIS
            </Typography>
          </Grid>
          <Grid item xs={8}>
            <Textfield name="bond" id="bond" label="VÍNCULO" disabled />
          </Grid>
          <Grid item xs={6}>
            <Textfield
              name="password"
              id="password"
              label="SENHA"
              type="password"
            />
          </Grid>
          <Grid item xs={6}>
            <Textfield
              name="checkPassword"
              id="checkPassword"
              label="REPETIR SENHA"
              type="password"
            />
          </Grid>
          <Grid item xs={12}>
            <Box pt={4}>
              <Typography
                variant="button"
                color="inherit"
                noWrap
                sx={{ fontSize: 20, fontWeight: 500 }}
              >
                DADOS BANCÁRIOS
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={8}>
            <Textfield name="bankAccount.bank" id="bank" label="BANCO" />
          </Grid>
          <Grid item xs={6}>
            <Textfield name="bankAccount.agency" id="agency" label="AGÊNCIA" />
          </Grid>
          <Grid item xs={6}>
            <Textfield
              name="bankAccount.checkingAccount"
              id="checkingAccount"
              label="CONTA CORRENTE"
            />
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

export const SignUpForm: FC = () => {
  const { user } = useUser();
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [newInitialValues, setNewInitialValues] = useState<{}>({
    ...user,
    ...(user.birthday && {
      birthday: Format.birthdate(user.birthday),
    }),
    ...(user.bond && {
      bond: user.bond === 'internal' ? 'Interno' : 'Externo',
    }),
    ...(!user.clinicName && {
      clinicName: '',
    }),
    ...(!user.clinicAddress && {
      clinicAddress: '',
    }),
    ...(!user.bankAccount && {
      bankAccount: {
        bank: '',
        agency: '',
        checkingAccount: '',
      },
    }),
  });

  const handleSubmit = (values: any) => {
    const birthday = Format.dateToISOString(values.birthday);
    const newValues = {
      ...values,
      birthday,
      bond: values.bond === 'Interno' ? 'internal' : 'external',
    };
    setLoading(true);
    setNewInitialValues(newValues);

    VetService.updateVet(newValues).then(
      ({ data }) => {
        setError('');
        setLoading(false);
        setSuccess(data.message);
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      },
      ({ response }) => {
        const errors = response.data.error;
        setSuccess('');
        setLoading(false);
        setError(errors);
      }
    );
  };

  return (
    <Formik
      initialValues={newInitialValues}
      validationSchema={vetUpdateValidation}
      onSubmit={handleSubmit}
    >
      <Form encType="multipart/form-data" style={{ width: '100%' }}>
        <Grid item xs={12} lg container spacing={1}>
          <Grid item xs={12} lg={6}>
            <LeftFormData loading={loading} user={user} />
          </Grid>
          <Grid item xs={12} lg={6}>
            <RightFormData loading={loading} />
          </Grid>
          {error && (
            <Grid container justifyContent="flex-end">
              <Grid item xs={6}>
                <FormMessage data={error} severity="error" />
              </Grid>
            </Grid>
          )}
          {success && (
            <Grid container justifyContent="flex-end">
              <Grid item xs={6}>
                <FormMessage data={success} severity="success" />
              </Grid>
            </Grid>
          )}
          <Grid item xs={12} container justifyContent="flex-end">
            <Button>SALVAR</Button>
          </Grid>
        </Grid>
      </Form>
    </Formik>
  );
};

